import React from "react";
import { motion } from "framer-motion";
import { ProgressRing } from "@/components/contracts/ProgressRing";
import { ReportSection, DeltaChip, Stat, Panel, Phase2Note, fmtHours } from "./ReportSection";
import { translateFr as tfr } from "@/i18n";

const FunnelBar = ({ stage, max, index, prevCount }) => {
  const pct = max ? Math.max((stage.count / max) * 100, stage.count > 0 ? 4 : 0) : 0;
  const conv = prevCount ? Math.round((stage.count / prevCount) * 100) : null;
  return (
    <div className="flex items-center gap-3" data-testid={`funnel-stage-${stage.key}`}>
      <span className="w-20 text-xs text-muted-foreground shrink-0">{stage.label}</span>
      <div className="flex-1 h-7 rounded-md bg-white/[0.03] overflow-hidden">
        <motion.div initial={{ width: 0 }} animate={{ width: `${pct}%` }}
          transition={{ duration: 0.45, delay: index * 0.06, ease: [0.4, 0, 0.2, 1] }}
          className="h-full rounded-md"
          style={{ background: `linear-gradient(90deg, rgba(229,32,26,0.55), rgba(229,32,26,${0.25 + index * 0.12}))` }} />
      </div>
      <span className="w-8 text-sm font-semibold tabular-nums text-right">{stage.count}</span>
      <span className="w-12 text-xs text-muted-foreground tabular-nums text-right hidden sm:block">
        {index > 0 && conv !== null ? `${conv}%` : ""}
      </span>
    </div>
  );
};

export const SectionSignature = ({ signature }) => {
  const funnel = signature?.funnel || [];
  const max = funnel[0]?.count || 0;
  const rate = signature?.signatureRate;

  return (
    <ReportSection number="02" title={tfr("Performance de signature")}
      purpose={tfr("Avec quelle efficacité vos contrats deviennent-ils signés ?")} testid="section-signature">
      <div className="grid lg:grid-cols-5 gap-4">
        <Panel className="lg:col-span-3" testid="signature-funnel-panel">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-5">Tunnel de finalisation <span className="normal-case font-normal tracking-normal">{tfr("(contrats créés sur la période)")}</span></p>
          {max === 0 ? (
            <p className="text-sm text-muted-foreground py-8 text-center" data-testid="funnel-empty">{tfr("Aucun contrat créé sur cette période.")}</p>
          ) : (
            <div className="space-y-2.5">
              {funnel.map((s, i) => <FunnelBar key={s.key} stage={s} max={max} index={i} prevCount={i > 0 ? funnel[i - 1].count : null} />)}
            </div>
          )}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-5 mt-6 pt-5 border-t border-white/[0.05]">
            <Stat label={tfr("Délai moyen de signature")} value={fmtHours(signature?.avgSigningDelayHours)} testid="sig-delay" />
            <Stat label={tfr("Délai moyen d'ouverture")} value={fmtHours(signature?.avgOpeningDelayHours)} testid="sig-open-delay" />
            <Stat label={tfr("Relances envoyées")} value={signature?.remindersSent} testid="sig-reminders" />
            <Stat label={tfr("Efficacité des relances")} testid="sig-reminder-eff"
              value={signature?.reminderEfficiency != null ? `${signature.reminderEfficiency}%` : "—"}
              sub={tfr("signés après ≥ 2 demandes")} />
          </div>
        </Panel>
        <Panel className="lg:col-span-2 flex flex-col" testid="signature-rate-panel">
          <p className="text-xs font-semibold uppercase tracking-[0.14em] text-muted-foreground mb-4">Taux de signature</p>
          <div className="flex items-center gap-5">
            <ProgressRing percent={rate ?? 0} size={104} stroke={7}
              color={rate == null ? "#71717A" : rate >= 60 ? "#22C55E" : rate >= 30 ? "#F5A524" : "#E5484D"}>
              <span className="text-xl font-semibold tabular-nums" data-testid="signature-rate-value">{rate != null ? `${rate}%` : "—"}</span>
            </ProgressRing>
            <div className="space-y-1.5">
              <DeltaChip delta={signature?.rateDelta} testid="signature-rate-delta" />
              <p className="text-xs text-muted-foreground leading-relaxed">{tfr("Part des contrats envoyés qui ont été signés par toutes les parties.")}</p>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-4 mt-6 pt-5 border-t border-white/[0.05]">
            <Stat label={tfr("En attente")} value={signature?.pendingSignatures} testid="sig-pending" />
            <Stat label={tfr("Refusées")} value={signature?.declinedSignatures} testid="sig-declined" />
            <Stat label={tfr("Expirés")} value={signature?.expiredContracts} testid="sig-expired" />
          </div>
          <div className="mt-auto pt-5">
            <Phase2Note testid="sig-esign-note">{signature?.esign?.note}</Phase2Note>
          </div>
        </Panel>
      </div>
    </ReportSection>
  );
};
